import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    IconCheck,
    IconCheckbox,
    IconBell,
    IconTrash
} from '@tabler/icons-react';
import Button from '../../../../components/ui/Button';
import './NotificationPage.css';
import { useNotifications, useNotificationActions, useDesktopNotifications } from '../../hooks/useNotifications';
import {
    selectFilteredNotifications,
    selectCurrentFilter,
    selectHasMore,
    selectNotificationsLoading,
    setFilter,
    fetchNotifications,
    markAllAsRead as markAllAsReadAction
} from '../../store/notificationsSlice';

import { useNotificationPageData } from './hooks/useNotificationPageData';
import { NotificationFilter } from './components/NotificationFilter';
import { NotificationList } from './components/NotificationList';

const NotificationsEnhanced = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.auth.currentUser);
    const authUserData = useSelector(state => state.auth.userData);

    const { unreadCount } = useNotifications();
    const { markAsRead, deleteNotification } = useNotificationActions();
    const { permission, requestPermission } = useDesktopNotifications();

    const filteredNotifications = useSelector(selectFilteredNotifications);
    const currentFilter = useSelector(selectCurrentFilter);
    const hasMore = useSelector(selectHasMore);
    const loading = useSelector(selectNotificationsLoading);
    const lastVisible = useSelector(state => state.notifications.lastVisible);

    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const dataProps = useNotificationPageData({
        currentUser,
        authUserData,
        filteredNotifications,
        navigate,
        markAsRead,
        deleteNotification
    });

    const handleFilterChange = (filter) => {
        dispatch(setFilter(filter));
    };

    const handleLoadMore = async () => {
        if (loading || isLoadingMore || !hasMore) return;
        setIsLoadingMore(true);
        try {
            await dispatch(fetchNotifications({
                userId: currentUser.uid,
                limitCount: 20,
                filter: currentFilter,
                lastDoc: lastVisible
            }));
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleMarkAllRead = () => {
        if (unreadCount > 0) {
            dispatch(markAllAsReadAction(currentUser.uid));
        }
    };

    const handleDesktopPermission = () => {
        requestPermission();
    };

    return (
        <div className="notifications-container fluid-container">
            <div className="notifications-header">
                <h2 className="notifications-title">Notifications</h2>

                <div className="notifications-actions">
                    {unreadCount > 0 && !dataProps.selectionMode && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleMarkAllRead}
                            title="Mark all as read"
                            className="rounded-full gap-2"
                        >
                            <IconCheck size={16} />
                            Mark all read
                        </Button>
                    )}
                    {!dataProps.selectionMode && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={dataProps.toggleSelectionMode}
                            title="Select notifications"
                            className="rounded-full gap-2"
                        >
                            <IconCheckbox size={16} />
                            Select
                        </Button>
                    )}
                    {permission === 'default' && !dataProps.selectionMode && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleDesktopPermission}
                            title="Enable desktop notifications"
                            className="rounded-full gap-2"
                        >
                            <IconBell size={16} />
                            Enable alerts
                        </Button>
                    )}
                    {dataProps.selectionMode && (
                        <>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={dataProps.selectAllVisible}
                                title="Select all visible"
                                className="rounded-full gap-2"
                            >
                                <IconCheckbox size={16} />
                                Select all
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={dataProps.handleBulkMarkRead}
                                title="Mark selected as read"
                                disabled={dataProps.selectedIds.size === 0}
                                className="rounded-full gap-2"
                            >
                                <IconCheck size={16} />
                                Mark read ({dataProps.selectedIds.size})
                            </Button>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={dataProps.handleBulkDelete}
                                title="Delete selected"
                                disabled={dataProps.selectedIds.size === 0}
                                className="rounded-full gap-2"
                            >
                                <IconTrash size={16} />
                                Delete ({dataProps.selectedIds.size})
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={dataProps.toggleSelectionMode}
                                title="Exit selection"
                                className="rounded-full"
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <NotificationFilter
                currentFilter={currentFilter}
                handleFilterChange={handleFilterChange}
                unreadCount={unreadCount}
            />

            <NotificationList
                loading={loading}
                combinedNotifications={dataProps.combinedNotifications}
                groupedNotifications={dataProps.groupedNotifications}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                handleLoadMore={handleLoadMore}
                userCache={dataProps.userCache}
                selectionMode={dataProps.selectionMode}
                selectedIds={dataProps.selectedIds}
                processingIds={dataProps.processingIds}
                openMenuId={dataProps.openMenuId}
                currentUser={currentUser}
                navigate={navigate}
                toggleSelected={dataProps.toggleSelected}
                handleNotificationClick={dataProps.handleNotificationClick}
                handleMarkRead={dataProps.handleMarkRead}
                handleDeleteNotification={dataProps.handleDeleteNotification}
                handleFollowRequestAccept={dataProps.handleFollowRequestAccept}
                handleFollowRequestReject={dataProps.handleFollowRequestReject}
                handleAccept={dataProps.handleAccept}
                handleDecline={dataProps.handleDecline}
                toggleMenu={dataProps.toggleMenu}
            />
        </div>
    );
};

export default NotificationsEnhanced;
